import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Swiper as SwiperInstance } from 'swiper/types';

import { cn } from '@/lib/utils';

import Badge from '@/components/atoms/badge';
import { Text } from '@/components/atoms/text';

import { gradientBorderStyling } from '@/styles/gradient';

import { CARD_ANIMATION_DURATION } from './constants';
import { Dots } from './Dots';
import { ProgressBar } from './ProgressBar';

export type CardItemProps = {
  badgeText?: string;
  cardTitle?: string;
  cardDescription?: React.ReactNode;
};

export type CardProps = {
  badgeText?: string;
  cardTitle?: string;
  cardDescription?: React.ReactNode;
  setActiveCard: Dispatch<SetStateAction<number>>;
  setIsCarouselClicked: Dispatch<SetStateAction<boolean>>;
  swiper: SwiperInstance | null;
  activeCard: number;
  items: CardItemProps[];
  isCarouselClicked: boolean;
};

export function CardItem({
  badgeText,
  cardDescription,
  cardTitle,
  swiper,
  activeCard,
  setActiveCard,
  setIsCarouselClicked,
  items,
  isCarouselClicked,
}: CardProps) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      if (progress < 100) {
        setProgress(progress + 1);
      } else {
        setProgress(0);
      }
    }, CARD_ANIMATION_DURATION);

    return () => clearInterval(interval);
  }, [progress]);

  return (
    <div
      className={cn(
        `bg-neutral-N00/12 border-gradient right-0 top-11 overflow-hidden rounded-3xl transition-opacity duration-500 ease-in-out ${gradientBorderStyling(
          {
            borderWidth: 'p-[1px]',
            borderRadius: 'rounded-3xl',
          }
        )}`
      )}
    >
      <div
        className={cn(
          'h-[296px] w-[374px]',
          'from-24% via-24% to-24% bg-gradient-to-br from-[#FE39C73D] via-[#5200FF3D] to-[#00F0D33D]',
          'flex flex-col justify-between rounded-3xl px-9 py-9'
        )}
      >
        <div className='fade-in-effect opacity-0'>
          <Badge
            variant='filled'
            classNames={{
              root: 'bg-neutral-N99 text-neutral-N00 border-neutral-N99 mb-3',
            }}
          >
            {badgeText}
          </Badge>
          <Text variant='h3' className='text-h3 text-neutral-N99 pb-4 '>
            {cardTitle}
          </Text>
          <p className='text-neutral-N99 pb-9'>{cardDescription}</p>
        </div>
        <div className='flex items-center gap-6'>
          <Dots
            items={items}
            isActive={activeCard}
            setActiveCard={setActiveCard}
            setIsCarouselClicked={setIsCarouselClicked}
            swiper={swiper}
          />
          {!isCarouselClicked && <ProgressBar progress={progress} />}
        </div>
      </div>
    </div>
  );
}
